interface CustomizationData {
  option_id: number;
  value: string;
}

export interface DeliveryAddressPayload {
  owner_type?: 'group' | 'group_order' | 'manual';
  recipient_name: string;
  recipient_email: string;
  recipient_phone: string;
  recipient_line_1: string;
  recipient_line_2?: string | null;
  recipient_city: string;
  recipient_postal_code: string;
  recipient_province: string;
  recipient_country: string;
}

interface CreatePaymentRequest {
  group_id: string;
  product_id: number;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customizations?: CustomizationData[];
  previous_order_id?: string;
  discount_codes?: string[];
  delivery_address?: DeliveryAddressPayload;
  delivery_choice?: 'individual' | 'grouped';
}

interface PaymentResponse {
  redsys_url: string;
  Ds_SignatureVersion: string;
  Ds_MerchantParameters: string;
  Ds_Signature: string;
  order_id?: string;
}

// URL base del backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class PaymentService {
  /**
   * Inicia un proceso de pago con Redsys
   * @param paymentData Datos necesarios para iniciar el pago
   * @returns Parámetros para redirigir a Redsys
   */
  async createPayment(paymentData: {
    name: string;
    email: string;
    phoneNumber: string;
    groupId: string;
    productId: number;
    totalPrice: number;
    customizations?: Array<{option_id: number; value: string; price?: number}>;
    previousOrderId?: string;
    discountCodes?: string[];
    deliveryAddress?: DeliveryAddressPayload;
    deliveryChoice?: 'individual' | 'grouped';
  }): Promise<PaymentResponse> {

    try {
      // Mapear las personalizaciones para eliminar propiedades que no usa el backend
      const mappedCustomizations = paymentData.customizations?.map((item: any) => ({
        option_id: item.option_id,
        value: item.value
      })) || [];
      
      const backendPayload: CreatePaymentRequest = {
        group_id: paymentData.groupId,
        product_id: paymentData.productId,
        customer_name: paymentData.name,
        customer_email: paymentData.email,
        customer_phone: paymentData.phoneNumber,
        customizations: mappedCustomizations,
      };

      // Añadir previous_order_id si existe
      if (paymentData.previousOrderId) {
        backendPayload.previous_order_id = paymentData.previousOrderId;
      }

      // Añadir códigos de descuento si existen
      if (paymentData.discountCodes && paymentData.discountCodes.length > 0) {
        backendPayload.discount_codes = paymentData.discountCodes;
      }

      if (paymentData.deliveryChoice === 'individual' || paymentData.deliveryChoice === 'grouped') {
        backendPayload.delivery_choice = paymentData.deliveryChoice;
      }

      if (
        paymentData.deliveryAddress?.recipient_name?.trim() &&
        paymentData.deliveryAddress?.recipient_line_1?.trim()
      ) {
        backendPayload.delivery_address = {
          ...paymentData.deliveryAddress,
          owner_type: 'group_order',
          recipient_country: paymentData.deliveryAddress.recipient_country || 'ES',
        };
      }
      
      // Construir la URL correcta
      const paymentUrl = `${API_BASE_URL}/api/payments/create`;
      
      // Realizar la solicitud
      const response = await fetch(paymentUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(backendPayload)
      });
      
      if (!response.ok) {
        if (response.headers.get('content-type')?.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Error ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
      }
      
      // Verificar si hay contenido y es JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Error al procesar la respuesta. Por favor, inténtalo de nuevo.');
      }
      
      const responseText = await response.text();
      if (!responseText) {
        throw new Error('No se recibió respuesta. Por favor, inténtalo de nuevo.');
      }
      
      try {
        const data = JSON.parse(responseText);
        return data;
      } catch (parseError) {
        throw new Error('Error al procesar la información. Por favor, inténtalo de nuevo.');
      }
    } catch (error: any) {
      // Manejar errores específicos
      let errorMessage: string;
      
      if (error.message && error.message.includes('Failed to fetch')) {
        // Esto generalmente indica un problema de conectividad o CORS
        errorMessage = 'No se pudo conectar con el servidor de pagos. Por favor, verifica tu conexión a internet o contacta a soporte.';
      } else {
        errorMessage = error.message || 'Error desconocido al procesar el pago';
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Redirige al usuario a la pasarela de pago de Redsys
   * @param paymentParams Parámetros devueltos por la API
   */
  redirectToRedsys(paymentParams: PaymentResponse): void {
    // Crear un formulario temporal para enviar los datos a Redsys
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = paymentParams.redsys_url;
    form.style.display = 'none';

    // Añadir los campos requeridos
    const signatureVersionField = document.createElement('input');
    signatureVersionField.name = 'Ds_SignatureVersion';
    signatureVersionField.value = paymentParams.Ds_SignatureVersion;
    form.appendChild(signatureVersionField);

    const merchantParamsField = document.createElement('input');
    merchantParamsField.name = 'Ds_MerchantParameters';
    merchantParamsField.value = paymentParams.Ds_MerchantParameters;
    form.appendChild(merchantParamsField);

    const signatureField = document.createElement('input');
    signatureField.name = 'Ds_Signature';
    signatureField.value = paymentParams.Ds_Signature;
    form.appendChild(signatureField);

    // Añadir el formulario al documento y enviarlo
    document.body.appendChild(form);
    form.submit();
  }

  extractRedsysOrderIdFromReturn(search: string): string | null {
    const params = new URLSearchParams(search);
    const encoded = params.get('Ds_MerchantParameters');
    if (!encoded) {
      return null;
    }
    try {
      const padded = encoded + '='.repeat((4 - (encoded.length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      return decoded.Ds_Order || decoded.DS_ORDER || decoded.DS_MERCHANT_ORDER || null;
    } catch {
      return null;
    }
  }

  enqueueRegistration(orderId: string): void {
    void fetch(`${API_BASE_URL}/api/payments/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ order_id: orderId }),
      keepalive: true,
    }).catch(() => undefined);
  }
}

export const paymentService = new PaymentService(); 